import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { notificationService } from '../services/notificationService';

export default function AdminNotificationsPopup({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const panelRef = useRef(null);

  const fetchMy = async () => {
    setLoading(true);
    try {
      const res = await notificationService.getMy();
      setItems(res?.data || res || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const unreadCount = Array.isArray(items) ? items.filter((n) => !n.isRead).length : 0;

  const markRead = async (id) => {
    try {
      await notificationService.markRead(id);
      fetchMy();
    } catch (e) {
      console.error(e);
      toast.error('Failed to update notification');
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchMy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    const onMouseDown = (e) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target)) return;
      onClose?.();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="absolute right-0 top-full mt-2 w-[340px] sm:w-[380px] z-50">
      <div
        ref={panelRef}
        className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="p-3 flex items-center justify-between border-b border-slate-100">
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">Notifications</p>
            <p className="text-[11px] text-slate-500">Unread: {unreadCount}</p>
          </div>
          <button
            onClick={() => onClose?.()}
            className="rounded-xl p-2 hover:bg-slate-100 transition"
            aria-label="Close notifications"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-slate-600">Loading...</div>
        ) : (
          <div className="max-h-[380px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">No notifications</div>
            ) : (
              <div className="divide-y">
                {items.map((n) => (
                  <div key={n._id} className="p-4 hover:bg-purple-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-800 truncate">{n.title}</div>
                        <div className="text-sm text-gray-600 mt-1 line-clamp-3">{n.message}</div>
                        <div className="text-xs text-gray-400 mt-2">
                          {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                        </div>
                      </div>

                      {!n.isRead && (
                        <button
                          onClick={() => markRead(n._id)}
                          className="shrink-0 px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all text-xs font-medium"
                        >
                          Mark read
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

